import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Star, StarOff, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AttachmentDisplay } from "@/components/AttachmentDisplay";

interface StarredMessagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, only show stars from this chat. Omit to show all. */
  chatId?: string;
  /** Called when the user picks a starred message (e.g. to scroll to it). */
  onJump?: (chatId: string, messageId: string) => void;
}

interface StarredRow {
  id: string;
  message_id: string;
  created_at: string;
  message: {
    id: string;
    chat_id: string;
    sender_id: string;
    original_text: string;
    attachment_url: string | null;
    created_at: string | null;
    sender_language: string;
  } | null;
  sender_profile?: {
    id: string;
    name: string;
    profile_image: string | null;
  } | null;
  chat_name?: string | null;
}

const getFileType = (url: string): "image" | "video" | "audio" | "document" => {
  const cleanUrl = url.replace("attachment:", "");
  const extension = cleanUrl.split(".").pop()?.toLowerCase()?.split("?")[0];
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(extension || "")) return "image";
  if (["mp4", "mov"].includes(extension || "")) return "video";
  if (["webm", "mp3", "ogg", "m4a", "wav", "aac", "3gp", "amr"].includes(extension || "")) return "audio";
  return "document";
};

export const StarredMessagesDialog = ({
  open,
  onOpenChange,
  chatId,
  onJump,
}: StarredMessagesDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<StarredRow[]>([]);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      // 1. Fetch the user's stars
      const { data: stars, error: starsErr } = await (supabase as any)
        .from("starred_messages")
        .select("id, message_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);

      if (cancelled) return;
      if (starsErr) {
        console.error("[StarredMessages] fetch stars error:", starsErr);
        setItems([]);
        setLoading(false);
        return;
      }

      const starList = (stars as { id: string; message_id: string; created_at: string }[]) || [];
      const messageIds = starList.map((s) => s.message_id);

      if (messageIds.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }

      // 2. Fetch the actual messages (RLS filters to ones the user can access)
      let messagesQuery = supabase
        .from("messages")
        .select("id, chat_id, sender_id, original_text, attachment_url, created_at, sender_language")
        .in("id", messageIds);
      if (chatId) {
        messagesQuery = messagesQuery.eq("chat_id", chatId);
      }
      const { data: messages, error: msgErr } = await messagesQuery;
      if (cancelled) return;
      if (msgErr) {
        console.error("[StarredMessages] fetch messages error:", msgErr);
        setItems([]);
        setLoading(false);
        return;
      }

      const msgMap = new Map((messages || []).map((m: any) => [m.id, m]));

      // 3. Fetch sender profiles + chat names in parallel
      const senderIds = Array.from(
        new Set((messages || []).map((m: any) => m.sender_id).filter(Boolean))
      );
      const chatIds = Array.from(
        new Set((messages || []).map((m: any) => m.chat_id).filter(Boolean))
      );

      const [profilesRes, chatsRes] = await Promise.all([
        senderIds.length
          ? supabase.from("profiles").select("id, name, profile_image").in("id", senderIds)
          : Promise.resolve({ data: [], error: null } as any),
        chatIds.length
          ? supabase.from("chats").select("id, name, is_group").in("id", chatIds)
          : Promise.resolve({ data: [], error: null } as any),
      ]);

      if (cancelled) return;

      const profileMap = new Map(
        ((profilesRes.data as any[]) || []).map((p) => [p.id, p])
      );
      const chatMap = new Map(((chatsRes.data as any[]) || []).map((c) => [c.id, c]));

      const composed: StarredRow[] = starList
        .map((s) => {
          const msg = msgMap.get(s.message_id);
          if (!msg) return null;
          const senderProfile = profileMap.get(msg.sender_id) || null;
          const chatRecord = chatMap.get(msg.chat_id) || null;
          return {
            id: s.id,
            message_id: s.message_id,
            created_at: s.created_at,
            message: msg,
            sender_profile: senderProfile,
            chat_name: chatRecord?.name ?? null,
          } as StarredRow;
        })
        .filter((row): row is StarredRow => row !== null);

      setItems(composed);
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, user, chatId]);

  const handleUnstar = async (starId: string) => {
    if (!user) return;
    const prev = items;
    setItems((cur) => cur.filter((i) => i.id !== starId));
    const { error } = await (supabase as any)
      .from("starred_messages")
      .delete()
      .eq("id", starId)
      .eq("user_id", user.id);
    if (error) {
      console.error("[StarredMessages] unstar error:", error);
      setItems(prev);
      toast({
        title: "Error",
        description: "Could not remove star.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            Starred messages
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-2 pb-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Star className="h-8 w-8 mb-2 opacity-60" />
                <p className="text-sm">No starred messages yet.</p>
                <p className="text-xs mt-1">Tap the star icon on any message to bookmark it.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const m = item.message!;
                  const senderName = item.sender_profile?.name || "Unknown";
                  const senderInitial = senderName[0] || "?";
                  return (
                    <div
                      key={item.id}
                      className="rounded-lg border bg-card p-3 hover:bg-accent/40 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={item.sender_profile?.profile_image || undefined} />
                          <AvatarFallback className="text-xs">{senderInitial}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1 flex-wrap">
                            <span className="font-medium text-foreground">{senderName}</span>
                            {item.chat_name && (
                              <>
                                <span>·</span>
                                <span className="truncate">{item.chat_name}</span>
                              </>
                            )}
                            {m.created_at && (
                              <>
                                <span>·</span>
                                <span>
                                  {formatDistanceToNow(new Date(m.created_at), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </>
                            )}
                          </div>
                          {m.original_text && (
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {m.original_text}
                            </p>
                          )}
                          {m.attachment_url && (
                            <div className="mt-2 max-w-xs">
                              <AttachmentDisplay
                                attachmentUrl={m.attachment_url}
                                getFileType={getFileType}
                                isOwnMessage={m.sender_id === user?.id}
                                senderLanguage={m.sender_language}
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {onJump && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  onJump(m.chat_id, m.id);
                                  onOpenChange(false);
                                }}
                              >
                                <MessageSquare className="h-3 w-3 mr-1" />
                                Open
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-muted-foreground"
                              onClick={() => handleUnstar(item.id)}
                            >
                              <StarOff className="h-3 w-3 mr-1" />
                              Unstar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="px-6 py-3 border-t flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
